/* Song-source helpers shared by the landing composers (Landing.tsx and
   the v2 landing's ComposerV2): sample songs, link-platform detection and
   the vocal-presentation inference sent along with a new project. */
import type { AudioOrigin } from "@/lib/copyrightConsent";

export type LibrarySong = {
  id: string;
  title?: string | null;
  genre?: string | null;
  prompt?: string | null;
  style?: string | null;
  duration?: number | null;
  audioUrl?: string | null;
  gender?: string | null;
  voiceGender?: string | null;
  vocalGender?: string | null;
  status?: string | null;
};
export type SourceState = null | {
  id?: string;
  type: "link" | "upload" | "mine";
  label: string;
  icon: "link" | "upload" | "music";
  meta?: string;
  /** Which platform a "link" source came from — decides whether the
   *  copyright confirmation is needed (Suno + demo tracks are exempt).
   *  Absent on old persisted drafts, which stay gated. */
  linkPlatform?: "youtube" | "tiktok" | "suno" | "demo" | "other";
  prompt?: string | null;
  style?: string | null;
  gender?: string | null;
  voiceGender?: string | null;
  vocalGender?: string | null;
  /** Real audio URL used when creating the project. For "upload" this is
   *  the S3 URL returned from uploadAiMusicVideoAsset; for "link" this is
   *  either the raw URL or the extracted MP3 URL (after yt-dlp). */
  audioUrl?: string;
};
export function sourceAudioOrigin(source: SourceState): AudioOrigin {
  if (!source) return "unknown";
  if (source.type === "mine") return "solmi_song";
  if (source.type === "upload") return "upload";
  switch (source.linkPlatform) {
    case "suno":
      return "suno";
    case "demo":
      return "demo";
    case "youtube":
      return "youtube";
    case "tiktok":
      return "tiktok";
    case "other":
      return "link";
    default:
      return "unknown";
  }
}


export const LANDING_SAMPLE_SONGS: LibrarySong[] = [
  {
    id: "track_1778078829330_4y76r1xn2",
    title: "Paper Boats",
    genre: "Folk, Longing, female vocals",
    style: "Folk, Longing, female vocals",
    audioUrl: "https://media.solmi.ai/music/1778078892172.mp3",
    status: "completed",
  },
  {
    id: "track_1778068068825_yppbo9rp9",
    title: "Floating Home",
    genre: "Ethereal dream pop, female vocals",
    style:
      "ethereal dream pop, 74 BPM, soft pads, harp textures, breathy vocal, cinematic atmosphere, gentle percussion, pastel production, female vocals",
    audioUrl: "https://media.solmi.ai/music/1778068173623.mp3",
    status: "completed",
  },
  {
    id: "track_1769154991306_x3lg0o0l2",
    title: "The Sound of Music",
    genre: "Classical",
    style: "Classical",
    audioUrl: "https://media.solmi.ai/music/1769155225296.mp3?v=20260817",
    status: "completed",
  },
  {
    id: "track_1764080309032_qim52cd97",
    title: "Sweet Dreams",
    genre: "Pop, Romantic, Quiet evening",
    style: "Pop, Romantic, Quiet evening",
    audioUrl: "https://media.solmi.ai/music/1764080409325.mp3?v=20260817",
    status: "completed",
  },
];

export type VoiceGender = "female" | "male" | "unknown";
export type VocalPresentationSource =
  | "user"
  | "project"
  | "song_metadata"
  | "analysis"
  | "brief_text"
  | "prompt_text"
  | "unknown";
export type VocalPresentation = {
  value: VoiceGender;
  source: VocalPresentationSource;
  confidence: number;
};

export function inferVoiceGenderFromText(value: string): VoiceGender | undefined {
  const text = value.toLowerCase();
  const female =
    /\b(female|woman|women|girl|lady|feminine)\s+(vocal|voice|singer|artist|lead|performer|idol|vocalist)s?\b/.test(
      text,
    ) ||
    /\b(female vocals|female voice|female singer|female vocalist|woman singer|girl singer)\b/.test(
      text,
    ) ||
    /(女声|女聲|女性ボーカル|女性歌手|女性歌声|女性聲音|女性声音|女歌手|女生歌手|女ボーカル|女主唱|女歌声|女歌聲|여성\s*(보컬|가수|목소리)|여자\s*(보컬|가수|목소리))/.test(
      text,
    );
  const male =
    /\b(male|man|men|boy|masculine)\s+(vocal|voice|singer|artist|lead|performer|idol|vocalist)s?\b/.test(
      text,
    ) ||
    /\b(male vocals|male voice|male singer|male vocalist|man singer|boy singer)\b/.test(
      text,
    ) ||
    /(男声|男聲|男性ボーカル|男性歌手|男性歌声|男性聲音|男性声音|男歌手|男生歌手|男ボーカル|男主唱|男歌声|男歌聲|남성\s*(보컬|가수|목소리)|남자\s*(보컬|가수|목소리))/.test(
      text,
    );
  if (female && !male) return "female";
  if (male && !female) return "male";
  return undefined;
}

export function normalizeVoiceGender(value: unknown): VoiceGender | undefined {
  const text = String(value || "")
    .trim()
    .toLowerCase();
  if (
    ["f", "female", "woman", "women", "girl", "女", "女声", "女聲"].includes(
      text,
    )
  ) {
    return "female";
  }
  if (["m", "male", "man", "men", "boy", "男", "男声", "男聲"].includes(text)) {
    return "male";
  }
  return inferVoiceGenderFromText(text);
}

export function vocalPresentationFromValue(
  value: VoiceGender | undefined,
  source: VocalPresentationSource,
  confidence: number,
): VocalPresentation | undefined {
  if (value !== "female" && value !== "male" && value !== "unknown") {
    return undefined;
  }
  return {
    value,
    source,
    confidence: Math.max(0, Math.min(1, confidence)),
  };
}

export function inferVocalPresentationFromText(
  value: string,
): VocalPresentation | undefined {
  const inferred = inferVoiceGenderFromText(value);
  return inferred
    ? vocalPresentationFromValue(inferred, "prompt_text", 0.6)
    : undefined;
}

// Platform detectors — keep in sync with server/youtubeDownloader.ts + DirectorHome.
export function isYouTubeUrl(url: string): boolean {
  // m. and music. links need the pre-create import too; without them the
  // landing composer treated m.youtube.com as a direct audio link.
  return /^(https?:\/\/)?((www|m|music)\.)?(youtube\.com|youtube-nocookie\.com|youtu\.be)\/.+/i.test(
    url,
  );
}
export function isTikTokUrl(url: string): boolean {
  return /^(https?:\/\/)?(www\.)?(tiktok\.com|vt\.tiktok\.com|vm\.tiktok\.com)\/.+/i.test(
    url,
  );
}
export function isSpotifyUrl(url: string): boolean {
  return /^(https?:\/\/)?(www\.)?(open\.spotify\.com|spotify\.link)\/.+/i.test(
    url,
  );
}
export function getYouTubeLinkError(url: string): string | null {
  let parsed: URL;
  try {
    const withProtocol = /^[a-z][a-z\d+\-.]*:\/\//i.test(url)
      ? url
      : `https://${url}`;
    parsed = new URL(withProtocol.trim().replace(/^["'`]+|["'`]+$/g, ""));
  } catch {
    return null;
  }

  const host = parsed.hostname.toLowerCase().replace(/^(www|m|music)\./, "");
  if (
    host !== "youtube.com" &&
    host !== "youtu.be" &&
    host !== "youtube-nocookie.com"
  ) {
    return null;
  }

  const videoId =
    host === "youtu.be"
      ? (parsed.pathname.split("/").filter(Boolean)[0] ?? "")
      : parsed.searchParams.get("v") ||
        parsed.pathname.match(/^\/(?:shorts|embed|live|v)\/([^/?#]+)/i)?.[1] ||
        "";
  const cleanedId = videoId.trim().replace(/^["'`]+|["'`]+$/g, "");
  if (/^[A-Za-z0-9_-]{11}$/.test(cleanedId)) return null;

  const legalPrefix = cleanedId.match(/^[A-Za-z0-9_-]*/)?.[0] ?? "";
  if (
    cleanedId.length < 11 ||
    (legalPrefix.length > 0 && legalPrefix.length < 11)
  ) {
    return "This YouTube link looks truncated. Copy the full video URL from YouTube and try again.";
  }
  return "Invalid YouTube link. Copy the full video URL from YouTube and try again.";
}
export function formatSongDuration(duration?: number | null): string {
  if (!duration || !Number.isFinite(duration)) return "";
  const m = Math.floor(duration / 60);
  const s = Math.floor(duration % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}
